import React, { useEffect, useState, useMemo } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Landmark, Building2, TreePine, Users2, ExternalLink, Calendar, CheckCircle2, Clock, Filter } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SCOPE_LABEL_IT = { national: "Nazionale", regional: "Regionale" };
const SCOPE_LABEL_EN = { national: "National", regional: "Regional" };

const TARGET_ICONS = {
  residential: Landmark,
  business: Building2,
  agriculture: TreePine,
  community: Users2,
};

const STATUS = {
  active: { label_it: "Attivo", label_en: "Active", color: "bg-[#166534] text-white" },
  upcoming: { label_it: "In arrivo", label_en: "Upcoming", color: "bg-[#EAB308] text-[#17301C]" },
  closed: { label_it: "Chiuso", label_en: "Closed", color: "bg-[#6E7A71] text-white" },
};

const FILTERS = [
  { key: "all", label_it: "Tutti", label_en: "All" },
  { key: "residential", label_it: "Residenziale", label_en: "Residential" },
  { key: "business", label_it: "Aziende", label_en: "Business" },
  { key: "agriculture", label_it: "Agricoltura", label_en: "Agriculture" },
  { key: "community", label_it: "CER", label_en: "CER" },
  { key: "regional", label_it: "Regionale", label_en: "Regional" },
];

export default function Grants() {
  const { lang } = useApp();
  const [grants, setGrants] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/grants").then(({ data }) => setGrants(data)); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return grants;
    if (filter === "regional") return grants.filter((g) => g.scope === "regional");
    return grants.filter((g) => g.target.includes(filter));
  }, [grants, filter]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-7xl mx-auto px-6 pt-12">
        <div className="mb-10 max-w-3xl">
          <div className="label-eco mb-3"><Landmark size={12} className="inline mr-1" /> Incentivi · Bandi</div>
          <h1 className="text-5xl font-black tracking-tight text-[#17301C]">
            {lang === "it" ? "Bandi e incentivi fotovoltaico" : "Photovoltaic grants & incentives"}
          </h1>
          <p className="text-lg text-[#3A4A3E] mt-3">
            {lang === "it"
              ? "Tutti gli incentivi disponibili in Italia per il tuo impianto: detrazioni, contributi a fondo perduto, bandi regionali. Aggiornati."
              : "All incentives available in Italy for your PV system: deductions, non-repayable grants, regional calls. Up to date."}
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-8" data-testid="grants-filters">
          <Filter size={14} className="text-[#6E7A71]" />
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`grants-filter-${f.key}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest transition-colors ${
                filter === f.key ? "bg-[#17301C] text-white" : "bg-white border border-[#DDE0D8] text-[#17301C] hover:bg-[#EAE7E0]"
              }`}
            >
              {lang === "it" ? f.label_it : f.label_en}
            </button>
          ))}
        </div>

        {/* Grants grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filtered.map((g, i) => {
            const targetKey = g.target.split("+")[0].split(" ")[0].split(",")[0].toLowerCase();
            const Icon = TARGET_ICONS[targetKey] || Landmark;
            const status = STATUS[g.status] || STATUS.active;
            return (
              <motion.button
                key={g.id}
                onClick={() => setSelected(g)}
                data-testid={`grant-${g.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="card-surface p-6 text-left hover:border-[#17301C]"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl bg-[#EAE7E0] flex items-center justify-center">
                    <Icon size={20} className="text-[#17301C]" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full ${status.color}`}>
                      {lang === "it" ? status.label_it : status.label_en}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border border-[#DDE0D8] text-[#3A4A3E]">
                      {g.scope === "regional" ? (lang === "it" ? SCOPE_LABEL_IT.regional : SCOPE_LABEL_EN.regional) : (lang === "it" ? SCOPE_LABEL_IT.national : SCOPE_LABEL_EN.national)}
                    </span>
                  </div>
                </div>
                <h3 className="text-xl font-black text-[#17301C] leading-tight mb-2">
                  {lang === "it" ? g.name_it : g.name_en}
                </h3>
                <div className="text-xs uppercase tracking-widest font-bold text-[#EAB308] mb-3">
                  {lang === "it" ? g.target_it : g.target_en}
                </div>
                <p className="text-sm text-[#3A4A3E] leading-relaxed line-clamp-3">
                  {lang === "it" ? g.amount_it : g.amount_en}
                </p>
                <div className="mt-4 flex items-center gap-2 text-xs text-[#6E7A71]">
                  <Calendar size={12} />
                  <span className="line-clamp-1">{lang === "it" ? g.deadline_it : g.deadline_en}</span>
                </div>
              </motion.button>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="card-surface p-12 text-center text-[#6E7A71]">
            {lang === "it" ? "Nessun bando in questa categoria." : "No grants in this category."}
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full ${STATUS[selected.status].color}`}>
                    {lang === "it" ? STATUS[selected.status].label_it : STATUS[selected.status].label_en}
                  </span>
                  <span className="text-xs uppercase tracking-widest font-bold text-[#EAB308]">
                    {lang === "it" ? selected.target_it : selected.target_en}
                  </span>
                </div>
                <DialogTitle className="text-3xl font-black text-[#17301C] leading-tight">
                  {lang === "it" ? selected.name_it : selected.name_en}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 mt-4">
                <Section title={lang === "it" ? "Importo e agevolazione" : "Amount & benefit"} icon={CheckCircle2}>
                  {lang === "it" ? selected.amount_it : selected.amount_en}
                </Section>
                <Section title={lang === "it" ? "Scadenza" : "Deadline"} icon={Clock}>
                  {lang === "it" ? selected.deadline_it : selected.deadline_en}
                </Section>
                <Section title={lang === "it" ? "Beneficiari" : "Eligibility"} icon={Users2}>
                  {lang === "it" ? selected.eligibility_it : selected.eligibility_en}
                </Section>
                {selected.link && (
                  <a href={selected.link} target="_blank" rel="noreferrer noopener" data-testid={`grant-link-${selected.id}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#17301C] hover:underline mt-2">
                    <ExternalLink size={14} /> {lang === "it" ? "Sito ufficiale" : "Official website"}
                  </a>
                )}
                <div className="text-xs text-[#6E7A71] border-t border-[#DDE0D8] pt-4 mt-4">
                  {lang === "it"
                    ? "⚠ Le informazioni sono indicative. Verificare sempre sul sito ufficiale prima di procedere e consultare un CAF."
                    : "⚠ Info is indicative. Always verify on the official website and consult a tax advisor."}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <div className="label-eco mb-2 flex items-center gap-2">
      <Icon size={12} /> {title}
    </div>
    <p className="text-[#3A4A3E] leading-relaxed">{children}</p>
  </div>
);
